module PortalLibs.Client.Components.NewTableNext



open FSharpPlus
open Elmish
open Fable.React
open Fable.React.Props
open Fulma
open Fulma.Extensions.Wikiki
open Fable.Core.JsInterop
open System
open Fable.FontAwesome
open Feliz
open PortalLibs.Client.Model
open PortalLibs.Shared.NewTable
open PortalLibs.Client.Util
open Feliz.MaterialUI
open PortalLibs.Shared
open PortalLibs.Client.Components.Checkradio


type Page = int

type PageSize = int

type ValueType<'Item> =
  | Text of ('Item -> string)
  | Custom of ('Item -> ReactElement)
  | CustomAligned of ('Item -> ReactElement)

type Alignment =
  | Center
  | Left
  | Right

type SelectionConfig<'SelectionAction> =
  { Icon: Fa.IconOption
    Tooltip: string
    SelectionActionType: 'SelectionAction }


type Selection<'Item, 'SelectionAction when 'Item: comparison> =
  | Active of SelectionConfig<'SelectionAction> * Map<'Item, unit>
  | Executing of SelectionConfig<'SelectionAction> * Map<'Item, unit>

module Selection =
  let getItems =
    function
    | Active (_, items) -> items
    | Executing (_, items) -> items

  let toActive =
    function
    | Active (config, items) -> Active(config, items)
    | Executing (config, items) -> Active(config, items)

type Footer<'Id> =
  | Text of string
  | Custom of ReactElement
  | Total of (float -> string)

type ColumnSort<'Item> =
  | NotSortable
  | ValueOrExternal
  | InMemory of ('Item -> string)

type HeaderType<'Item> =
  | Text of string
  | StyledText of string * CSSProp list
  | CheckboxLabel of string * ('Item -> bool) * ('Item list * bool -> unit)

module HeaderType =
  let noDecoration str =
    StyledText(str, [ TextDecoration "none" ])

type Column<'Item, 'Id> =
  { Header: HeaderType<'Item>
    Align: Alignment
    Value: ValueType<'Item>
    Id: 'Id
    Sortable: ColumnSort<'Item>
    Draggable: bool
    InitiallyVisible: bool
    MinWidth: int option
    Footer: Footer<'Id> option }

type Model<'Item, 'Id, 'SelectionAction when 'Item: comparison and 'Id: comparison> =
  { Page: Page
    PageSize: PageSize
    Sort: Sort<'Id> option
    TotalItems: int64
    IsLoading: bool
    Selection: Selection<'Item, 'SelectionAction> option
    ColumnOrder: ('Id * bool) list
    ColumnMoving: 'Id option
    HoveringOver: int option
    Totals: Map<'Id, float>
    Key: string }

type ExternalMsg<'Item, 'Id, 'SelectionAction> =
  | NoOp
  | ExecuteSelection of 'SelectionAction * 'Item list
  | ColumnsChanged of ('Id * bool) list
  | PagingOrSortChanged


type Msg<'Item, 'Id, 'SelectionAction> =
  | PagingOrSortChanged of Page * PageSize * Sort<'Id> option
  | SelectionActive of SelectionConfig<'SelectionAction> option
  | ToggleItemSelection of 'Item
  | SelectItems of 'Item list
  | DeselectAll
  | ConfirmSelection
  | ColumnMove of 'Id
  | CancelMove
  | ColumnDrop of int * Column<'Item, 'Id> list
  | ColumnVisibility of 'Id * Column<'Item, 'Id> list
  | HoveringOver of int option

type Paged =
  { HidePaginationUnder1Page: bool
    PageSizeOptions: int [] option }

type Paging =
  | Paged of Paged
  | NotPaged

type DataLoad =
  | OnDemand
  | InMemory

type Config<'Item, 'Id, 'SelectionAction when 'Item: comparison and 'Id: comparison> =
  { Model: Model<'Item, 'Id, 'SelectionAction>
    Columns: Column<'Item, 'Id> list
    ColumnSelection: bool
    Items: 'Item list
    ItemKey: 'Item -> string
    SubRows: ('Item -> ReactElement list) option
    Selection: SelectionConfig<'SelectionAction> list * bool
    Paging: Paging
    Dispatch: Msg<'Item, 'Id, 'SelectionAction> -> unit
    RowClick: ('Item -> unit) option
    DataLoad: DataLoad
    StickyHeader: {| ZIndex: int |} option
    Key: string }

let init columnsOpt =
  { Page = 0
    PageSize = 25
    Sort = None
    TotalItems = -1L
    IsLoading = false
    Selection = None
    ColumnOrder = columnsOpt |> Option.defaultValue []
    ColumnMoving = None
    HoveringOver = None
    Totals = Map.empty
    Key = Guid.NewGuid().ToString() }

let resetModel model =
  { model with
      Page = 0
      TotalItems = -1L
      Selection = None }

let update<'Item, 'Id, 'SelectionAction when 'Item: comparison and 'Id: equality and 'Id: comparison>
  (msg: Msg<'Item, 'Id, 'SelectionAction>)
  (model: Model<'Item, 'Id, 'SelectionAction>)
  : Model<'Item, 'Id, 'SelectionAction> * Cmd<Msg<'Item, 'Id, 'SelectionAction>> * ExternalMsg<'Item, 'Id, 'SelectionAction> =
  match msg with
  | PagingOrSortChanged (page, pageSize, sort: Sort<'Id> option) ->
    if page <> model.Page then
      scrollIntoView $"sv-table-%s{model.Key}"


    { model with
        Page = if sort <> model.Sort then 0 else page
        PageSize = pageSize
        Sort = sort
        IsLoading = true
        Selection = None },
    Cmd.none,
    ExternalMsg.PagingOrSortChanged
  | SelectionActive selection ->
    { model with
        Selection =
          selection
          |>> (fun selection -> Active(selection, Map.empty)) },
    Cmd.none,
    NoOp
  | ToggleItemSelection item ->
    { model with
        Selection =
          model.Selection
          |>> (fun selection ->
            match selection with
            | Executing _ -> selection
            | Active (selection, items) ->
              let newItems =
                if Map.containsKey item items then
                  Map.remove item items
                else
                  Map.add item () items

              Active(selection, newItems)) },
    Cmd.none,
    NoOp
  | SelectItems items ->
    { model with
        Selection =
          model.Selection
          |>> (fun selection ->
            match selection with
            | Executing _ -> selection
            | Active (selection, _) ->
              let newItems =
                items
                |>> (fun item -> item, ())
                |> Map.ofList

              Active(selection, newItems)) },
    Cmd.none,
    NoOp
  | DeselectAll ->
    { model with
        Selection =
          model.Selection
          |>> (fun selection ->
            match selection with
            | Executing _ -> selection
            | Active (selection, _) -> Active(selection, Map.empty)) },
    Cmd.none,
    NoOp
  | ConfirmSelection ->
    { model with
        Selection =
          model.Selection
          |>> (fun selection ->
            match selection with
            | Executing _ -> selection
            | Active (selection, items) -> Executing(selection, items)) },
    Cmd.none,
    (match model.Selection with
     | Some selection ->
       match selection with
       | Executing _ -> NoOp
       | Active (selectionType, items) ->
         ExecuteSelection(selectionType.SelectionActionType, (items |> Map.toKeyList))
     | None -> NoOp)
  | ColumnMove id' -> { model with ColumnMoving = Some id' }, Cmd.none, NoOp
  | HoveringOver id' ->
    { model with HoveringOver = model.ColumnMoving >>= (fun _ -> id') }, Cmd.none, NoOp
  | CancelMove ->
    { model with
        ColumnMoving = None
        HoveringOver = None },
    Cmd.none,
    NoOp
  | ColumnDrop (idx, cols) ->
    let newModel =
      match model.ColumnMoving with
      | Some droppedCol ->
        let previousOrder =
          if model.ColumnOrder.IsEmpty then
            cols
            |>> (fun col -> col.Id, col.InitiallyVisible)
          else
            model.ColumnOrder

        let newOrder =
          let newOrderTemp =
            previousOrder
            |> mapi (fun i (colId, visible) ->
              [ if i = idx then droppedCol, true
                if colId <> droppedCol then
                  colId, visible ])
            |> List.concat

          if idx = previousOrder.Length then
            List.append newOrderTemp [ droppedCol, true ]
          else
            newOrderTemp

        { model with
            ColumnMoving = None
            HoveringOver = None
            ColumnOrder = newOrder }
      | None -> model

    newModel, Cmd.none, ColumnsChanged newModel.ColumnOrder
  | ColumnVisibility (id, cols) ->
    let previousOrder =
      if model.ColumnOrder.IsEmpty then
        cols
        |>> (fun col -> col.Id, col.InitiallyVisible)
      else if model.ColumnOrder
              |> List.exists (fun (colId, _) -> id = colId) then
        model.ColumnOrder
      else
        (id, false) :: model.ColumnOrder

    let newModel =
      { model with
          ColumnOrder =
            previousOrder
            |>> (fun (colId, vis) ->
              if colId = id then
                colId, not vis
              else
                colId, vis) }

    newModel, Cmd.none, ColumnsChanged newModel.ColumnOrder




let NewTableCheckboxCell =
  React.memo (
    "NewTableCheckboxCell",
    (fun (props: {| Key: string
                    EditMode: ControlEditMode<bool>
                    Label: string option
                    Value: bool |}) ->
      CheckboxComp
        {| Key = $"%s{props.Key}-CheckBoxComp"
           EditMode = props.EditMode
           Label = props.Label
           Value = props.Value |}),
    (fun (props: {| Key: string
                    EditMode: ControlEditMode<bool>
                    Label: string option
                    Value: bool |}) -> props.Key),
    areEqual = Helpers.equalsButFunctions
  )


let isSelectionActive selectionOpt =
  match selectionOpt with
  | None -> false
  | Some selection ->
    match selection with
    | Active _ -> true
    | Executing _ -> false

let isSelectionExecuting selectionOpt =
  match selectionOpt with
  | None -> false
  | Some selection ->
    match selection with
    | Active _ -> false
    | Executing _ -> true

let sortColumns config =
  if config.Model.ColumnOrder.IsEmpty then
    config.Columns
    |>> (fun col -> col, col.InitiallyVisible)
  else
    config.Model.ColumnOrder
    |> List.choose (fun (id, visible) ->
      config.Columns
      |> List.tryFind (fun col -> col.Id = id)
      |>> (fun col -> col, visible))

let private dropTargetCol idx config =
  th [ OnDragOver(fun ev -> ev.preventDefault ())
       OnDragEnter(fun _ -> HoveringOver(Some idx) |> config.Dispatch)
       OnDragLeave(fun _ -> HoveringOver None |> config.Dispatch)
       OnDrop(fun _ -> ColumnDrop(idx, config.Columns) |> config.Dispatch)
       Style [
         BackgroundColor(
           if config.Model.HoveringOver.IsSome
              && (config.Model.HoveringOver |> Option.defaultValue 0) = idx then
             "green"
           else
             "lightgray"
         )
       ] ] []


let headerControl column items =
  match column.Header with
  | Text header -> span [] [ header |> str ]
  | StyledText (header, cssProps) ->
    span [ Style cssProps ] [
      header |> str
    ]
  | CheckboxLabel (header, getVal, onCheck) ->
    let allUnchecked =
      items |> List.forall (getVal >> not)

    Html.div [
      prop.style [
        style.display.flex
        style.alignItems.stretch
      ]
      prop.children [
        NewTableCheckboxCell
          {| Key = $"NewTableCheckboxHeader-%s{column.Id.ToString()}"
             EditMode =
              ControlEditMode.Edit
                { OnChange =
                    (fun checked' ->
                      (items
                       |> List.filter (fun item ->
                         checked' |> Option.defaultValue false
                         <> getVal item),
                       checked' |> Option.defaultValue false)
                      |> onCheck)
                  StopPropagation = true }
             Label = None
             Value = not allUnchecked |}
        Html.span [
          prop.style [ style.marginLeft 15 ]
          prop.text header
        ]
      ]
    ]

let private header column items config =
  th [ Draggable column.Draggable
       OnDragEnd(fun _ -> CancelMove |> config.Dispatch)
       OnDragStart (fun ev ->
         ev.dataTransfer.setData ("text/plain", "dummy")
         |> ignore

         ColumnMove column.Id |> config.Dispatch)
       //OnDragOver (fun ev -> ev.preventDefault(); )
       //OnDrop (fun _ -> CancelMove |> config.Dispatch)
       Style [
         TextAlign(
           match column.Align with
           | Center -> TextAlignOptions.Center
           | Left -> TextAlignOptions.Left
           | Right -> TextAlignOptions.Right
         )
         VerticalAlign "middle"
         match column.MinWidth with
         | Some width -> Width $"%i{width}px"
         | None -> ()
       ] ] [
    match column.Sortable with
    | NotSortable -> headerControl column items
    | _ ->
      let customStyles =
        match column.Header with
        | StyledText (_, cssProps) -> cssProps
        | _ -> []

      a [ Style(Color "black" :: customStyles)
          OnClick (fun _ ->
            let sort =
              match config.Model.Sort with
              | None -> Some(Asc column.Id)
              | Some sort ->
                match sort with
                | Asc id ->
                  if id = column.Id then
                    Some(Desc column.Id)
                  else
                    Some(Asc column.Id)
                | Desc id ->
                  if id = column.Id then
                    None
                  else
                    Some(Asc column.Id)

            (config.Model.Page, config.Model.PageSize, sort)
            |> PagingOrSortChanged
            |> config.Dispatch) ] [
        div [ Style [
                Display DisplayOptions.Flex
                JustifyContent(
                  match column.Align with
                  | Center -> "center"
                  | Left -> "flex-start"
                  | Right -> "flex-end"
                )
                AlignItems AlignItemsOptions.Center
              ] ] [
          headerControl column items
          match config.Model.Sort with
          | None -> nothing
          | Some sort ->
            let sortDiv content =
              div [ Style [
                      MarginLeft "5px"
                      Color "#3273dc"
                    ] ] [
                content
              ]

            match sort with
            | Asc id ->
              if id = column.Id then
                sortDiv (
                  Icon.icon [ Icon.Size ISize.IsSmall ] [
                    Fa.i [ Fa.Solid.ChevronDown ] []
                  ]
                )
            | Desc id ->
              if id = column.Id then
                sortDiv (
                  Icon.icon [ Icon.Size ISize.IsSmall ] [
                    Fa.i [ Fa.Solid.ChevronUp ] []
                  ]
                )
        ]
      ]
  ]


let private RowGeneric<'Item, 'Id, 'SelectionAction when 'Item: comparison and 'Id: equality> =
  React.memo (
    "NewTableRow",
    (fun (props: {| Item: 'Item
                    SubRows: bool
                    SortedColumns: (Column<'Item, 'Id> * bool) list
                    Selection: Selection<'Item, 'SelectionAction> option
                    Dispatch: Msg<'Item, 'Id, 'SelectionAction> -> unit
                    ColumnMoving: 'Id option
                    RowClick: ('Item -> unit) option
                    Key: string |}) ->
      let dropDestCol =
        td [ Style [ BorderBottom "0" ] ] []

      tr [ HTMLAttr.Id props.Key
           TabIndex 0
           match props.RowClick with
           | None -> ()
           | Some click ->
             OnClick(fun _ -> click props.Item)

             OnKeyDown(
               ifEnter (fun e ->
                 e.stopPropagation ()
                 click props.Item)
             )

             Style [ Cursor "pointer" ] ] [
        match props.Selection with
        | None -> nothing
        | Some selection ->
          let isSelected =
            Selection.getItems selection
            |> Map.containsKey props.Item

          td [ Style [
                 VerticalAlign "middle"
                 if props.SubRows then BorderBottom "0"
               ] ] [
            NewTableCheckboxCell
              {| Key = $"NewTableSelectionCheckbox-%s{props.Key}"
                 EditMode =
                  match selection with
                  | Active _ ->
                    ControlEditMode.Edit
                      { OnChange = (fun _ -> ToggleItemSelection props.Item |> props.Dispatch)
                        StopPropagation = true }
                  | Executing _ -> ControlEditMode.ReadOnly
                 Label = None
                 Value = isSelected |}
          ]

        yield!
          (props.SortedColumns
           |> mapi (fun i (column, visible) ->
             [ match props.ColumnMoving with
               | Some movingCol ->
                 if i = 0 && movingCol <> column.Id then
                   dropDestCol
               | None -> ()
               if visible then
                 td [ Style [
                        VerticalAlign "middle"
                        if props.SubRows then BorderBottom "0"
                        match column.Value with
                        | ValueType.Custom _ -> Padding "0"
                        | _ -> ()
                      ] ] [
                   div [ Style [
                           Display DisplayOptions.Flex
                           AlignItems AlignItemsOptions.Center
                           match column.Value with
                           | ValueType.Text _
                           | CustomAligned _ ->
                             JustifyContent(
                               match column.Align with
                               | Center -> "center"
                               | Left -> "flex-start"
                               | Right -> "flex-end"
                             )
                           | _ -> ()
                         ] ] [
                     match column.Value with
                     | ValueType.Text strValue -> strValue props.Item |> str
                     | ValueType.Custom custom -> custom props.Item
                     | ValueType.CustomAligned custom ->

                       custom props.Item
                   ]
                 ]

                 match props.ColumnMoving with
                 | Some movingCol ->
                   if movingCol <> column.Id
                      && (i + 1 >= props.SortedColumns.Length
                          || (fst (props.SortedColumns.Item(i + 1))).Id
                             <> movingCol) then
                     dropDestCol
                 | None -> () ])
           |> List.concat)
      ]),
    (fun (props: {| Item: 'Item
                    SubRows: bool
                    SortedColumns: (Column<'Item, 'Id> * bool) list
                    Selection: Selection<'Item, 'SelectionAction> option
                    Dispatch: Msg<'Item, 'Id, 'SelectionAction> -> unit
                    ColumnMoving: 'Id option
                    RowClick: ('Item -> unit) option
                    Key: string |}) -> props.Key),
    areEqual = Helpers.equalsButFunctions
  )


let selectionButton config dispatch =
  Button.button [ Button.OnClick(fun _ -> SelectionActive(Some config) |> dispatch)
                  Button.Props [
                    Style [
                      Width "25px"
                      Height "30px"
                      Padding "0 0 0 1px"
                      MarginRight "5px"
                    ]
                    Tooltip.dataTooltip config.Tooltip
                  ]
                  Button.CustomClass Tooltip.ClassName ] [
    Icon.icon [ Icon.Size ISize.IsSmall ] [
      Fa.i [ config.Icon ] []
    ]
  ]


let ColumnSelectionGeneric<'Item, 'Id, 'SelectionAction when 'Item: comparison and 'Id: comparison> =
  React.memo (
    "ColumnSelection",
    (fun (props: {| ColumnSelection: bool
                    Columns: Column<'Item, 'Id> list
                    ActiveColumns: ('Id * bool) list
                    Dispatch: Msg<'Item, 'Id, 'SelectionAction> -> unit |}) ->
      if props.ColumnSelection then
        Dropdown.dropdown [ Dropdown.IsHoverable
                            Dropdown.IsRight ] [
          Dropdown.trigger [] [
            Button.button [ Button.Props [
                              Style [ Width "25px"; Height "30px" ]
                            ] ] [
              Icon.icon [ Icon.Size IsSmall ] [
                Fa.i [ Fa.Solid.Bars ] []
              ]
            ]
          ]
          Dropdown.menu [] [
            Dropdown.content
              []
              (props.Columns
               |>> (fun column ->
                 Dropdown.Item.a [ Dropdown.Item.Props [
                                     OnClick (fun e ->
                                       e.preventDefault ()

                                       ColumnVisibility(column.Id, props.Columns)
                                       |> props.Dispatch)
                                   ] ] [
                   Checkradio.checkbox [ Checkradio.Id(Guid.NewGuid().ToString())
                                         Checkradio.Checked(
                                           props.ActiveColumns
                                           |> List.tryFind (fun (id, _) -> column.Id = id)
                                           |>> snd
                                           |> Option.defaultValue false
                                         )
                                         Checkradio.OnChange ignore ] [
                     str (
                       match column.Header with
                       | Text string -> string
                       | StyledText (string, _) -> string
                       | CheckboxLabel (string, _, _) -> string
                     )
                   ]
                 ]))
          ]
        ]
      else
        nothing),
    areEqual = Helpers.equalsButFunctions
  )


let pageSize (config: Paged) pageSize =
  config.PageSizeOptions
  >>= (fun pageSizeOptions ->
    if Array.contains pageSize pageSizeOptions then
      Some pageSize
    else
      Array.tryHead pageSizeOptions)
  |> Option.defaultValue pageSize


let pagination config totalItems =

  let shouldShowPagination paginationConfig =
    paginationConfig.HidePaginationUnder1Page
    && totalItems > (int64 config.Model.PageSize)
    || not paginationConfig.HidePaginationUnder1Page

  match config.Paging with
  | Paged paginationConfig when shouldShowPagination paginationConfig ->
    let pageSize =
      pageSize paginationConfig config.Model.PageSize

    Mui.tablePagination [
      tablePagination.component' "div"

      tablePagination.count (int totalItems)

      if totalItems = -1L then
        tablePagination.labelDisplayedRows (fun args -> $"%i{args.from} - %i{args.from + pageSize}" |> str)

      tablePagination.page config.Model.Page

      tablePagination.rowsPerPage pageSize

      let availablePageSizes =
        match paginationConfig.PageSizeOptions with
        | Some availablePageSizes -> [| yield! availablePageSizes |]
        | None -> [| pageSize |]

      tablePagination.rowsPerPageOptions availablePageSizes

      tablePagination.onChangePage (fun _ page ->
        (page, pageSize, config.Model.Sort)
        |> PagingOrSortChanged
        |> config.Dispatch)

      tablePagination.onChangeRowsPerPage (fun pageSize ->
        (config.Model.Page, !!pageSize?target?value, config.Model.Sort)
        |> PagingOrSortChanged
        |> config.Dispatch)
    ]
  | _ -> nothing

let sortItems sortOpt columns items =
  match sortOpt with
  | None -> items
  | Some sort ->
    let colId =
      match sort with
      | Asc id -> id
      | Desc id -> id

    let columnOpt =
      columns
      |> List.tryFind (fun column -> column.Id = colId)

    match columnOpt with
    | None -> items
    | Some column ->
      let sortFun item =
        match column.Sortable with
        | NotSortable -> ""
        | ValueOrExternal ->
          match column.Value with
          | ValueType.Text strVal -> strVal item
          | ValueType.Custom _
          | ValueType.CustomAligned _ -> ""
        | ColumnSort.InMemory sortVal -> sortVal item

      match sortOpt with
      | None -> items
      | Some sort ->
        match sort with
        | Asc _ -> items |> List.sortBy sortFun
        | Desc _ -> items |> List.sortByDescending sortFun


let NewTableNextGeneric<'Item, 'Id, 'SelectionAction when 'Item: comparison and 'Id: comparison> =
  let ColumnSelectionSpecific =
    ColumnSelectionGeneric<'Item, 'Id, 'SelectionAction>

  let RowSpecific =
    RowGeneric<'Item, 'Id, 'SelectionAction>

  Feliz.React.memo (
    "NewTableNext",
    (fun (props: {| Config: Config<'Item, 'Id, 'SelectionAction> |}) ->
      let anythingSelected =
        props.Config.Model.Selection
        |>> (fun sel ->
          match sel with
          | Active (_, items) -> not items.IsEmpty
          | _ -> false)
        |> Option.defaultValue false

      let sortedColumns = sortColumns props.Config

      div [ Style [
              Position PositionOptions.Relative
            ] ] [
        div [ Style [
                Display DisplayOptions.Flex
                JustifyContent "space-between"
              ] ] [
          div [ Style [
                  PaddingBottom "3px"
                  Display DisplayOptions.Flex
                  AlignItems AlignItemsOptions.Center
                ] ] [
            let selectionList, selectionActive =
              props.Config.Selection

            if not selectionList.IsEmpty then

              match props.Config.Model.Selection with
              | None ->
                if selectionActive then
                  yield!
                    selectionList
                    |>> (fun selectionConfig -> selectionButton selectionConfig props.Config.Dispatch)
                else
                  loadingWithMessageHoriz "Loading data..."


              | Some selection ->
                match selection with
                | Active _ ->
                  Button.button [ Button.Disabled(not anythingSelected)
                                  Button.OnClick(fun _ -> ConfirmSelection |> props.Config.Dispatch)
                                  Button.Props [
                                    Style [
                                      Width "25px"
                                      Height "30px"
                                      Padding "0"
                                      Color "green"
                                    ]
                                  ] ] [
                    Icon.icon [ Icon.Size ISize.IsSmall ] [
                      Fa.i [ Fa.Solid.Check ] []
                    ]
                  ]

                  Button.button [ Button.OnClick(fun _ -> SelectionActive None |> props.Config.Dispatch)
                                  Button.Props [
                                    Style [
                                      Width "25px"
                                      Height "30px"
                                      Padding "0"
                                      MarginLeft "3px"
                                    ]
                                  ] ] [
                    Icon.icon [ Icon.Size ISize.IsSmall ] [
                      Fa.i [ Fa.Solid.Times ] []
                    ]
                  ]
                | Executing _ -> loadingWithMessageHoriz "Executing..."

                let config =
                  match selection with
                  | Active (selection, _) -> selection
                  | Executing (selection, _) -> selection

                div [ Style [
                        Width "25px"
                        Height "30px"
                        Padding "3px 5px"
                        BorderLeft "1px solid black"
                        MarginLeft "5px"
                      ] ] [
                  Icon.icon [ Icon.Size ISize.IsSmall
                              Icon.Props [
                                Tooltip.dataTooltip config.Tooltip
                              ]
                              Icon.CustomClass Tooltip.ClassName ] [
                    Fa.i [ config.Icon ] []
                  ]
                ]
          ]
          div [] [
            ColumnSelectionSpecific
              {| ColumnSelection = props.Config.ColumnSelection
                 Columns = props.Config.Columns
                 ActiveColumns =
                  if props.Config.Model.ColumnOrder.IsEmpty then
                    props.Config.Columns
                    |>> (fun col -> col.Id, col.InitiallyVisible)
                  else
                    props.Config.Model.ColumnOrder
                 Dispatch = props.Config.Dispatch |}
          ]
        ]
        Standard.table [ Id $"sv-table-%s{props.Config.Model.Key}"
                         ClassName "table is-striped is-hoverable"
                         Style [
                           if props.Config.StickyHeader.IsSome then BorderCollapse "Separate"
                           Width "100%"
                           MarginBottom "5px"
                           if props.Config.DataLoad = DataLoad.OnDemand
                              && props.Config.Model.IsLoading then
                             Filter "blur(2px)"
                         ] ] [
          thead [] [
            Html.tr [
              prop.style [
                match props.Config.StickyHeader with
                | Some v -> yield! [style.position.sticky; style.top 0; style.zIndex v.ZIndex; style.backgroundColor.white]
                | None -> ()
              ]
              prop.children [
                match props.Config.Model.Selection with
                | None -> nothing
                | Some selection ->
                  let items = Selection.getItems selection

                  let allSelected =
                    props.Config.Items
                    |> List.forall (fun item -> Map.containsKey item items)

                  th [ Style [ VerticalAlign "middle" ] ] [
                    NewTableCheckboxCell
                      {| Key = "NewTableSelectionCheckboxHeader"
                         EditMode =
                          match selection with
                          | Active _ ->
                            ControlEditMode.Edit
                              { OnChange =
                                  (fun _ ->
                                    (if allSelected then
                                       DeselectAll
                                     else
                                       SelectItems props.Config.Items)
                                    |> props.Config.Dispatch)
                                StopPropagation = false }
                          | Executing _ -> ControlEditMode.ReadOnly
                         Label = None
                         Value = allSelected |}
                  ]


                yield!
                  (sortedColumns
                   |> mapi (fun i (column, visible) ->
                     [ match props.Config.Model.ColumnMoving with
                       | Some movingCol ->
                         if i = 0 && movingCol <> column.Id then
                           dropTargetCol i props.Config
                       | None -> ()
                       if visible then
                         header column props.Config.Items props.Config

                         match props.Config.Model.ColumnMoving with
                         | Some movingCol ->
                           if movingCol <> column.Id
                              && (i + 1 >= sortedColumns.Length
                                  || (fst (sortedColumns.Item(i + 1))).Id
                                     <> movingCol) then
                             dropTargetCol (i + 1) props.Config
                         | None -> () ])
                   |> List.concat)
              ]
            ]
          ]
          tbody
            []
            (if props.Config.Items.IsEmpty
                && props.Config.Model.IsLoading then
               [ tr [ Style [ Height "75px" ] ] [] ]
             else
               (props.Config.Items
                |> fun items ->
                     (match props.Config.DataLoad with
                      | OnDemand -> items
                      | InMemory ->
                        sortItems props.Config.Model.Sort props.Config.Columns items
                        |> fun items ->
                             match props.Config.Paging with
                             | NotPaged -> items
                             | Paged pagingConfig ->
                               let pageSize =
                                 pageSize pagingConfig props.Config.Model.PageSize

                               items
                               |> List.skip (pageSize * props.Config.Model.Page)
                               |> List.truncate pageSize)
                     |> List.collect (fun item ->
                       let subRows =
                         props.Config.SubRows
                         |>> (fun subRows -> subRows item)
                         |> Option.defaultValue []
                         |> mapi (fun i reactElement ->
                           ReactTransitionGroup.AnimatedCreate
                             {| Key =
                                 $"NewTableAnimatedExpand-%s{props.Config.Key}-Row-%s{props.Config.ItemKey item}-%i{i}"
                                ContentOpt = Some reactElement |})

                       [ RowSpecific(
                           {| Item = item
                              SubRows = not subRows.IsEmpty
                              SortedColumns = sortedColumns
                              Selection = props.Config.Model.Selection
                              Dispatch = props.Config.Dispatch
                              ColumnMoving = props.Config.Model.ColumnMoving
                              RowClick = props.Config.RowClick
                              Key = $"NewTable-%s{props.Config.Key}-Row-%s{props.Config.ItemKey item}" |}
                         )
                         yield! subRows ])))
          if props.Config.Columns
             |> List.exists (fun col -> Option.isSome col.Footer) then
            tfoot [] [
              tr
                []
                (sortedColumns
                 |>> (fun (column, visible) ->
                   if visible then
                     td [ Style [ VerticalAlign "middle" ] ] [
                       div [ Style [
                               Display DisplayOptions.Flex
                               JustifyContent "flex-end"
                             ] ] [
                         span [] [
                           strong [] [
                             match column.Footer with
                             | None -> nothing
                             | Some footer ->
                               match footer with
                               | Footer.Text str' -> str str'
                               | Custom elem -> elem
                               | Total totalView ->
                                 match props.Config.Model.Totals |> Map.tryFind column.Id with
                                 | None ->
                                   Icon.icon [ Icon.Props [
                                                 Style [ Color "gray"; MarginTop "-1px" ]
                                               ] ] [
                                     Fa.i [ Fa.Solid.Spinner; Fa.Pulse ] []
                                   ]
                                 | Some total -> str (totalView total)
                           ]
                         ]
                       ]
                     ]
                   else
                     nothing))
            ]
        ]
        pagination
          props.Config
          (match props.Config.DataLoad with
           | OnDemand -> props.Config.Model.TotalItems
           | InMemory -> props.Config.Items.Length |> int64)


        if props.Config.DataLoad = DataLoad.OnDemand
           && props.Config.Model.IsLoading then
          div [ Style [
                  Position PositionOptions.Absolute
                  Top "0"
                  CSSProp.Left "0"
                  Width "100%"
                  Height "calc(100% - 50px)"
                ] ] [
            Heading.h6 [ Heading.Option.Props [
                           Style [
                             Position PositionOptions.Absolute
                             Top "42%"
                             CSSProp.Left "48%"
                           ]
                         ] ] [
              loadingWithMessage "Loading..."
            ]
          ]
      ]),
    (fun (props: {| Config: Config<'Item, 'Id, 'SelectionAction> |}) -> props.Config.Key),
    areEqual = Helpers.equalsButFunctions
  )
